import React from 'react';
import { Lock, Eye, ShieldCheck, Database, UserCheck } from 'lucide-react';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12 pb-24">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs font-semibold">
          <Lock className="w-3.5 h-3.5 text-cyan-400" />
          <span>User Privacy & Security</span>
        </div>
        <h1 className="text-4xl font-black text-white tracking-tight">Privacy Policy</h1>
        <p className="text-slate-400 text-xs">
          Effective Date: January 1, 2026 | Compliant with GDPR, CCPA, and Global Privacy Standards
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-8 sm:p-12 space-y-8 text-slate-300 text-sm leading-relaxed">
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-cyan-400" />
            <span>1. Information We Collect</span>
          </h2>
          <p>
            At <strong>GSR STUDIO</strong>, we respect your privacy and minimize data collection to what is essential for running reliable, high-performance multiplayer games and websites:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-slate-400 text-xs">
            <li>
              <strong>Account & Profile Data:</strong> Username, email address, avatar selection, and linked platform IDs (e.g. Steam, Discord).
            </li>
            <li>
              <strong>Gameplay & Diagnostic Telemetry:</strong> Hardware configurations, frame rate benchmarks, network latency, crash reports, and gameplay progression.
            </li>
            <li>
              <strong>Web Inquiries:</strong> Name and email provided when submitting contact transmissions or support tickets.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-purple-400" />
            <span>2. How We Use Your Data</span>
          </h2>
          <p>
            Your information is utilized strictly to provide, improve, and protect our games:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-slate-400 text-xs">
            <li>Facilitating ultra-low latency peer matchmaking and leaderboards via Cloudflare D1 and global edge workers.</li>
            <li>Detecting anti-cheat violations, automated bot farms, and malicious exploits.</li>
            <li>Analyzing crash diagnostics to release game performance patches.</li>
            <li>Responding to support tickets, partnership inquiries, and career applications.</li>
          </ul>
          <p className="font-semibold text-white text-xs pt-1">
            We will never sell, rent, or trade your personal information to third-party data brokers or advertisers.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <span>3. Edge Data Storage & Encryption</span>
          </h2>
          <p>
            Data processed across GSR STUDIO services is protected with TLS 1.3 in-transit and AES-256 encryption at-rest. Our cloud infrastructure leverages Cloudflare global network security with built-in DDoS mitigation and isolated serverless runtime environments.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-cyan-400" />
            <span>4. Your Data Rights (GDPR / CCPA)</span>
          </h2>
          <p>
            Regardless of where you reside, you retain complete authority over your personal information:
          </p>
          <ul className="list-disc pl-5 space-y-1.5 text-slate-400 text-xs">
            <li><strong>Right of Access:</strong> Request a full copy of your player profile and telemetry log.</li>
            <li><strong>Right to Rectification:</strong> Update or correct inaccurate account details.</li>
            <li><strong>Right to Erasure (&ldquo;Right to be Forgotten&rdquo;):</strong> Request full deletion of your player account and associated database records.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-indigo-400" />
            <span>5. Contact Data Privacy Officer</span>
          </h2>
          <p>
            To exercise your data rights or report a privacy concern, submit a request directly to our Data Protection Officer at{' '}
            <span className="text-cyan-400 font-semibold">privacy@gsrstudio.games</span>.
          </p>
        </section>
      </div>
    </div>
  );
};
